import React, { useState, useEffect } from "react";
import { Row, Col, Select, Button, Input, DatePicker, message, TimePicker } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import "./DataFilter.css";
import * as _ from "lodash";
import { returnDataArrayObject } from "../../../../../utils/arrayHelper";
import {
  DATE_FORMAT,
  DATE_WITH_HOUR_FORMAT,
  DATE_WITH_HOUR_MINUTES_FORMAT,
} from "../../../../../constants/dateConstants";
import NotFound from "../../../../../components/NotFound";
import { updateDataFilters, updateResourceSourceAndFilter, deleteFilters } from "../../../../../redux/editorSlice";
import moment from "moment";
import { customDateTimeFormat } from "../../../../../utils/filterHelper";
import DeleteIcon from "../../../../../assets/icons/delete-icon.svg";
import { DEFAULT_DATE_DISPLAY, buildDatePeriods } from "../../../../../utils/dateFilterDisplay";
const { Option } = Select;

/* The panel is 300px wide, so a Select popup constrained to its trigger truncates
   "Greater Than Or Equal" to "Greate...". Letting the popup size itself keeps the
   options readable without widening the column. */
const WIDE_DROPDOWN = { minWidth: 190 };

/* Defaults for a global filter that has no config yet. Kept in one place because the
   creation path and the load path used to disagree - text got maxLength 100 when you
   picked the type and 20 when you reopened the panel. */
function defaultConfigFor(dataType) {
  if (dataType === "number") return { min: 0, max: 10000 };
  if (dataType === "date") return { range: "dynamic", value: "LastMonth" };
  return { maxLength: 100 };
}

/* Data type of a column, from the resource's fields. Returns undefined rather than
   throwing when the column is gone. */
function findFieldDataType(selectedResource, fieldName) {
  const field =
    selectedResource?.availableFields?.find((af) => af?.fieldName === fieldName) ??
    selectedResource?.allFields?.find((af) => af?.fieldName === fieldName);
  return field?.dataType;
}

/* Which operators a data filter can use. Strings can only be matched or searched;
   ordering operators need a comparable type. */
function filterTypesFor(dataType) {
  return dataType === "string" ? ["equal", "con"] : ["equal", "lt", "gt", "lte", "gte", "between"];
}

/* ── DISPLAY DATE FILTER (PRM-517) ──────────────────────────────────────────────
   A preview of the date control the READER gets at runtime. It is driven entirely by
   the Date-type global filters already in the list - there is deliberately no "add
   range" button here, and nothing in this box changes how a filter is sent.

   Two date filters make one range, four make two. Pairing is by list order: filters
   1+2 are the first period, 3+4 the second. An odd one out is shown greyed, because a
   range needs both ends.

   The picker itself is NOT built here. The editor only has to show the shape the
   reader will see; the working control belongs to witmeg-reporting-embed-v2, which
   reads `dateFilterDisplay` off the saved resource to know which shape to draw.

   Built from plain HTML elements and `ddf-` prefixed classes ONLY - no antd, no
   design-system components. The editor sidebar carries blanket overrides such as

     .editor-left-sidebar .ant-input { border: ...; height: 32px !important; ... }

   which reach into any antd control rendered here and cannot be beaten from a
   component stylesheet. Anything added to this box should stay plain for the same
   reason, and keep the `ddf-` prefix so no other rule can select it by accident. */

/* Moved to utils/dateFilterDisplay.js so the board view and the embed package pair
   ranges the same way this box previews them. Re-exported: callers already import
   it from here. */
export { DEFAULT_DATE_DISPLAY, buildDatePeriods };

/* Inline SVG rather than @ant-design/icons, for the reason above. */
const IconCalendar = () => (
  <svg className="ddf-icon" viewBox="0 0 16 16" aria-hidden="true">
    <rect x="2" y="3" width="12" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <path
      d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

const IconChevron = ({ up }) => (
  <svg className="ddf-icon" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d={up ? "M4 10l4-4 4 4" : "M4 6l4 4 4-4"}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconPencil = () => (
  <svg className="ddf-icon" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M11.3 2.2l2.5 2.5L6 12.5l-3 .5.5-3z" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
  </svg>
);

/* Each period gets its own accent so two ranges are told apart at a glance - the
   mockups colour Period 1 and Period 2 differently for the same reason. */
const PERIOD_ACCENTS = ["#1d4ed8", "#047857", "#b45309", "#7c3aed"];

/* See parameterNameIssue. Off until it can tell a genuine typo from a parameter the
   endpoint accepts without the resource declaring it. */
const SHOW_UNKNOWN_PARAM_WARNING = false;



export default function DataFilter(props) {
  const [dataFilters, setDataFilters] = useState([]);
  const [definedFilters, setDefinedFilters] = useState([]);
  const [fields, setFields] = useState([]);
  /* How the reader's date control should be drawn, and what each period is called.
     Saved on the resource next to customFilters so the embed package can read it. */
  const [dateDisplay, setDateDisplay] = useState(DEFAULT_DATE_DISPLAY);
  const [dateBoxOpen, setDateBoxOpen] = useState(true);
  // Index of the period whose name is being renamed, or null. See the pen icon.
  const [editingPeriod, setEditingPeriod] = useState(null);
  // const [selectedResource, setSelectedResource] = useState(null);
  // const [reportDataStatus, setReportDataStatus] = useState('default');
  const dateTimeFormatChange = customDateTimeFormat(props.selectedResource.availableFields);

  const dispatch = useDispatch();

  const setDataFiltertem = () => {
    let currentFilters = [...dataFilters];
    currentFilters.push({
      id: Date.now(),
      dataType: "string",
      filterType: "equal",
    });
    setDataFilters(currentFilters);
  };

  const deleteDataFilterItem = (item) => {
    setDataFilters(dataFilters.filter((f) => f.id !== item.id));
    dispatch(
      deleteFilters({
        id: item.id,
        filterType: "dataFilters",
      }),
    );
  };

  // useEffect(() => {
  //   let currentFilters = dataFilters.map((df) => {
  //     return {
  //       id: df.id,
  //       fieldName: df.fieldName,
  //       displayName: df.displayName,
  //       dataType: df.dataType,
  //       filterType: df.dataType==='string'? 'equal' : df.filterType,
  //       value: df.value ? df.value : null,
  //       value1: df.value1 ? df.value1 : null,
  //       value2: df.value2 ? df.value2 : null,
  //     };
  //   })
  //   setDataFilters(currentFilters)
  // }, [dataFilters])

  useEffect(() => {
    console.log("availableFields changed", props.selectedResource.dataFilters);
    let currentFilters = (props?.selectedResource?.dataFilters || []).map((df) => {
      return {
        id: df.id,
        fieldName: df.fieldName,
        displayName: df.displayName,
        /* Was: availableFields.find(...)["dataType"] with no guard on the result, so
           opening this panel threw as soon as a saved filter pointed at a column that
           had since been removed from the resource. */
        dataType: df.dataType || findFieldDataType(props?.selectedResource, df.fieldName) || "text",
        filterType: df.filterType,
        value: df.value ? df.value : null,
        value1: df.value1 ? df.value1 : null,
        value2: df.value2 ? df.value2 : null,
      };
    });

    const currentDefinedFilters = (props?.selectedResource?.customFilters || []).map((df) => {
      return {
        id: df.id,
        parameterType: df.parameterType,
        parameterName: df.parameterName,
        dataType: df.dataType,
        value: df.value,
        /* Was: config rebuilt from dataType with hardcoded constants, which threw away
           whatever was saved - every reopen reset Min/Max/Max Length and forced the date
           range back to LastMonth. Now the saved config wins and the defaults are only a
           fallback for filters that have none. */
        config: _.isEmpty(df.config) ? defaultConfigFor(df.dataType) : df.config,
      };
    });

    setDefinedFilters(currentDefinedFilters);

    /* Seeded from the resource so reopening the panel keeps the author's choice.
       Missing on every board saved before this existed, hence the default. */
    setDateDisplay({
      ...DEFAULT_DATE_DISPLAY,
      ...(props?.selectedResource?.dateFilterDisplay || {}),
    });
    setDataFilters(currentFilters);
  }, [props.selectedResource.availableFields]);

  const updateDataFilter = (item, key, val) => {
    // let currentFilters = [...dataFilters];
    // let updatedFilter = Object.assign(item, { [key]: val });

    // // updatedFilter[key] = val
    // currentFilters[currentFilters.indexOf(f => f.id === item.id)] = updatedFilter
    let currentFilters = dataFilters.map((df) => {
      if (df?.id !== item?.id) return { ...df };

      const nextDataType =
        key === "dataType"
          ? val
          : key === "fieldName"
            ? (findFieldDataType(props?.selectedResource, val) ?? df.dataType)
            : df.dataType;

      /* Changing the type invalidates what came before it: "Greater Than" makes no
         sense on a string, and a date string left in a number filter would be saved
         and shipped to the runtime as-is. Both are reset rather than carried over. */
      const typeChanged = nextDataType !== df.dataType;
      const allowed = filterTypesFor(nextDataType);
      const requestedType = key === "filterType" ? val : df.filterType;

      return {
        id: df.id,
        fieldName: key === "fieldName" ? val : df?.fieldName,
        displayName: key === "displayName" ? val : df?.displayName,
        dataType: nextDataType,
        filterType: allowed.includes(requestedType) ? requestedType : "equal",
        value: key === "value" ? val : typeChanged ? null : (df.value ?? null),
        value1: key === "value1" ? val : typeChanged ? null : (df.value1 ?? null),
        value2: key === "value2" ? val : typeChanged ? null : (df.value2 ?? null),
      };
    });

    setDataFilters(currentFilters);
  };

  const setDefinedFiltertem = () => {
    let currentFilters = [...definedFilters];
    currentFilters.push({
      id: Date.now(),
      parameterType: "body",
      parameterName: "",
      dataType: "text",
      config: {},
    });
    setDefinedFilters(currentFilters);
  };

  const deleteDefinedFilterItem = (item) => {
    setDefinedFilters(definedFilters.filter((f) => f.id !== item.id));
    dispatch(
      deleteFilters({
        id: item.id,
        filterType: "customFilters",
      }),
    );
  };

  const updateDefinedFilter = (item, key, val) => {
    // let currentFilters = [...definedFilters];
    // let updatedFilter = Object.assign(item, { [key]: val });

    // // updatedFilter[key] = val
    // currentFilters[currentFilters.indexOf(f => f.id === item.id)] = updatedFilter

    /* Rewritten. The old nested-ternary chain could only ever write config when the
       dataType key changed, which is why Min / Max / Max Length were unreachable, and
       it dropped `value` entirely (see the commented-out branch below) so editing any
       field wiped a value that was already set. */
    let currentFilters = definedFilters.map((df) => {
      if (df.id !== item.id) return { ...df };

      const next = { ...df, [key]: val };

      if (key === "dataType") {
        // A config for the old type means nothing to the new one.
        next.config = defaultConfigFor(val);
        next.value = null;
      } else if (key === "min" || key === "max" || key === "maxLength") {
        /* These are bounds the runtime compares against, so they are stored as numbers -
           an input always hands back a string, and "10" > "9" is false. */
        next.config = { ...df.config, [key]: val === "" || val === null ? null : Number(val) };
        delete next[key];
      } else if (key === "dynamicDateRange") {
        /* Switching between a rolling window and a fixed date: the two carry different
           payloads in config.value, so it is reset to that mode's default. */
        next.config = { range: val, value: val === "dynamic" ? "LastMonth" : null };
        delete next[key];
      } else if (key === "dynamicDateRangeValue") {
        next.config = { ...df.config, range: df.config?.range || "dynamic", value: val };
        delete next[key];
      }

      return next;
    });

    // value:
    //   df.id === item.id && key === "value"
    //     ? df.dataType === "number"
    //       ? Number(val)
    //       : df.dataType === "date" ?
    //         moment(val).format("yyyy-MM-DD[T]HH:mm:ss.SSSZ")
    //         : val
    //     : df.dataType === "number"
    //       ? Number(df.value)
    //       : df.value,

    setDefinedFilters(currentFilters);
  };

  // useEffect(() => {
  //     dispatch(updateDataFilters(dataFilters))
  // }, [dataFilters]);

  /* The parameters this resource actually sends, as "type:name" - body params and
     url params kept apart because a global filter picks one destination or the other.
     Same path the editor's own fetch reads (ResourceEditor/index.js). Empty for a
     resource that declares none, which is why the warning below is skipped then. */
  const declaredParameterNames = () => {
    const requestValues = props?.selectedResource?.source?.connectionData?.requestValues;
    const names = new Set();

    (requestValues?.bodyParams || []).forEach((p) => p?.key && names.add(`body:${String(p.key).trim()}`));
    (requestValues?.urlParams || []).forEach((p) => p?.key && names.add(`query:${String(p.key).trim()}`));

    return names;
  };

  /**
   * What is wrong with one global filter's parameter name, live.
   *
   * "duplicate" is fatal - the request is built as obj[name] = value, so two filters
   * with one name means one value is silently dropped. Scoped per destination: a Body
   * X and a Query X go to different places and do not collide.
   *
   * "unknown" is only a warning. An endpoint may well accept a parameter this resource
   * never declared, so it must stay savable - but it is also the mistake that costs
   * days, because a name the API does not read produces a request that succeeds and
   * filters nothing.
   */
  const parameterNameIssue = (filter) => {
    const name = String(filter?.parameterName || "").trim();
    if (!name) return null;

    const key = `${filter.parameterType}:${name}`;
    const firstIndex = definedFilters.findIndex(
      (f) => `${f.parameterType}:${String(f.parameterName || "").trim()}` === key,
    );
    const thisIndex = definedFilters.findIndex((f) => f.id === filter.id);

    if (firstIndex !== -1 && thisIndex !== -1 && firstIndex !== thisIndex) {
      return { kind: "duplicate", message: "Already used by another filter above." };
    }

    /* The "not a parameter this resource sends" warning is OFF for now. The rule is
       sound - a name the endpoint does not read gives a request that succeeds and
       filters nothing - but a resource legitimately accepts parameters it never
       declared, so it fired on correct setups too. Flip SHOW_UNKNOWN_PARAM_WARNING
       to re-enable; nothing else has to change. */
    if (SHOW_UNKNOWN_PARAM_WARNING) {
      const declared = declaredParameterNames();
      // nothing to compare against on a resource that declares no parameters
      if (declared.size > 0 && !declared.has(key)) {
        return { kind: "unknown", message: "Not a parameter this resource sends." };
      }
    }

    return null;
  };

  const saveFilters = (filterArray, customFilters) => {
    console.log("Save FILTERS", customFilters);

    /* A row with no field picked saved as fieldName: undefined and then silently did
       nothing at runtime - the host has no column to attach it to. Same for a global
       filter with no parameter name: it cannot be put in a request. Say so instead of
       writing a filter that can never fire. */
    if (filterArray.some((f) => !f.fieldName)) {
      message.error("Every data filter needs a field name.");
      return;
    }

    if (customFilters.some((f) => !f.parameterName)) {
      message.error("Every global filter needs a parameter name.");
      return;
    }

    /* Two filters writing the same parameter is always broken: the request is built
       as `obj[parameterName] = value`, so one of them silently overwrites the other
       and only one value is ever sent. Scoped per destination - a Body `X` and a
       Query `X` go to different places and do not collide. */
    const seen = new Set();
    const duplicate = customFilters.find((f) => {
      const key = `${f.parameterType}:${String(f.parameterName).trim()}`;
      if (seen.has(key)) return true;
      seen.add(key);
      return false;
    });

    if (duplicate) {
      message.error(`"${duplicate.parameterName}" is used by more than one ${duplicate.parameterType} filter.`);
      return;
    }

    /* The unknown-name warning is drawn on the row itself now (parameterNameIssue),
       so there is no toast here - a message beside the field it is about beats one
       that floats at the top of the page and fades. The duplicate check above still
       blocks the save, because that one cannot be allowed through at all. */

    if (customFilters && customFilters.length > 0) {
      console.log("APPLY FILTERS customFilters");
      fetchReportData(filterArray, customFilters)
        .then((res) => message.info("Filters successfully saved!"))
        .catch((err) => console.log(err));
      // dispatch(updateDataFilters({ definedFilters: definedFilters, filterArray: filterArray }))
    } else {
      dispatch(updateDataFilters({ dataFilters: filterArray, applyFilters: true }));
      message.info("Filters successfully saved!");
    }
  };

  const clearFilters = () => {
    dispatch(updateDataFilters({ dataFilters: [], customFilters: [], applyFilters: false }));
    setDataFilters([]);
    /* Was missing, so Reset cleared redux but left the global rows on screen - and
       pressing Save again wrote them straight back. */
    setDefinedFilters([]);
    // With no date filters left there is no range to draw, so the choice goes too.
    setDateDisplay(DEFAULT_DATE_DISPLAY);
    message.info("Filters successfully cleared!");
    /* Passed explicitly rather than left to the state above: setDateDisplay does not
       apply until the next render, so fetchReportData would still read - and save -
       the choice this Reset is supposed to be clearing. */
    fetchReportData([], [], DEFAULT_DATE_DISPLAY);
    //     .then((res) => message.info("Filters successfully cleared!"))
    //     .catch((err) => console.log(err));
  };

  async function fetchReportData(filterArray, customFilters, dateDisplayOverride) {
    // console.log(" fetchReportData===>>>>", resources.selectedResource)
    const selectedResource = props.selectedResource;

    const payload = {
      selectedResource: Object.assign(selectedResource),
      // reportData: reportData,
      customFilters: customFilters,
      dataFilters: filterArray,
      /* PRM-517: the shape the reader's date control should take. Display only - it
         changes nothing about how a filter is sent. Save Filters writes whatever is
         on screen; Reset passes the cleared default in (see clearFilters). */
      dateFilterDisplay: dateDisplayOverride || dateDisplay,
    };

    dispatch(updateResourceSourceAndFilter(payload));
    // if (
    //   selectedResource &&
    //   selectedResource.source &&
    //   selectedResource.source.resoueceType === "Rest API"
    // ) {
    //   // const response = await axios.get(resources.selectedResource.connectionData.baseUrl);
    //   // console.log(" fetchReportData===>>>>", selectedResource.source)
    //   try {
    //     const requestValues =
    //       selectedResource.source.connectionData.requestValues;
    //     const authInfo =
    //       selectedResource &&
    //       selectedResource.source.connectionData &&
    //       selectedResource.source.connectionData.authInfo;
    //     let urlParamSring = "";
    //     if (requestValues.urlParams.length > 0) {
    //       urlParamSring = urlParamSring + "?";
    //       for (let i = 0; i < requestValues.urlParams.length; i++) {
    //         if (
    //           requestValues.urlParams[i]["key"] !== "" &&
    //           requestValues.urlParams[i]["value"] !== ""
    //         ) {
    //           urlParamSring =
    //             urlParamSring +
    //             requestValues.urlParams[i]["key"] +
    //             "=" +
    //             requestValues.urlParams[i]["value"] +
    //             "&";
    //         }
    //       }
    //       urlParamSring.slice(0, -1);
    //     }

    //     let headerObj = {};
    //     if (requestValues.requestHeaders.length > 0) {
    //       for (let i = 0; i < requestValues.requestHeaders.length; i++) {
    //         if (
    //           requestValues.requestHeaders[i]["key"] !== "" &&
    //           requestValues.requestHeaders[i]["value"] !== ""
    //         ) {
    //           headerObj[requestValues.requestHeaders[i]["key"]] =
    //             requestValues.requestHeaders[i]["value"];
    //         }
    //       }
    //     }

    //     let bodyParamObj = {};
    //     // if (requestValues.authParams) {
    //     //     bodyParamObj = Object.assign({}, authParams);
    //     // }
    //     if (requestValues.bodyParams.length > 0) {
    //       for (let i = 0; i < requestValues.bodyParams.length; i++) {
    //         if (
    //           requestValues.bodyParams[i]["key"] !== "" &&
    //           requestValues.bodyParams[i]["value"] !== ""
    //         ) {
    //           bodyParamObj[requestValues.bodyParams[i]["key"]] =
    //             requestValues.bodyParams[i]["value"];
    //         }  else if ( requestValues.bodyParams[i]["key"] !== "" && requestValues.bodyParams[i]["dataType"] === "date") {
    //           if(requestValues.bodyParams[i]["dateConfig"]) {
    //             let dateCf = requestValues.bodyParams[i]["dateConfig"];
    //             if(dateCf.dateOption === "daysBefore") {
    //               let dateVal = moment.utc().subtract(dateCf.timeUnits, 'day').startOf('day').format(dateCf.dateFormat);
    //               bodyParamObj[requestValues.bodyParams[i]["key"]] = dateVal;
    //             } else if (dateCf.dateOption === "today") {
    //               let dateVal = moment().format(dateCf.dateFormat);
    //               bodyParamObj[requestValues.bodyParams[i]["key"]] = dateVal;
    //             }
    //           }
    //         }
    //       }
    //     }
    //     // // await Axios.get(resources.selectedResource.connectionData.baseUrl)
    //     if (customFilters.length > 0) {
    //       customFilters.forEach((element) => {
    //         if (element.parameterName !== "") {
    //           if (element.parameterType === "query") {
    //             if (urlParamSring === "") {
    //               urlParamSring = urlParamSring + "?";
    //             }
    //             urlParamSring =
    //               urlParamSring +
    //               element.parameterName +
    //               "=" +
    //               element.value +
    //               "&";
    //           } else {
    //             bodyParamObj[element.parameterName] = element.value;
    //           }
    //         }
    //       });
    //     }
    //     // setReportDataStatus("pending")
    //     if (authInfo.method === "jwt") {
    //       console.log("JWT PROCESS");
    //       const timestamp = Math.floor(Date.now() / 1000) + 1000;
    //       const privateKey = authInfo.parameters.privateKey;
    //       let userToken=localStorage.getItem('accessToken');
    //       var payload = {
    //         aud: authInfo.parameters.aud,
    //         exp: timestamp,
    //         iss: authInfo.parameters.iss,
    //         userToken: userToken
    //       };
    //       var signOptions = {
    //         algorithm: "RS256",
    //       };

    //       var privateKeyVal = privateKey
    //         ? privateKey.replace(/\\n/g, "\n")
    //         : null;

    //       if (privateKeyVal) {
    //         try {
    //           var token = jwt.sign(payload, privateKeyVal, signOptions);
    //           console.log("Generated JWT authParams=====>>", token);
    //           // setApiTestStatus("success")

    //           await Axios({
    //             method: requestValues.httpMethod,
    //             url:
    //               selectedResource.source.connectionData.baseUrl +
    //               urlParamSring,
    //             data: JSON.parse(JSON.stringify(bodyParamObj)),
    //             headers: {
    //               "Access-Control-Allow-Origin": "*",
    //               "Content-Type": "application/json",
    //               Authorization: `Bearer ` + token,
    //             },
    //           })
    //             .then((response) => {
    //               console.log("CUSTOM FILTER response.data", response);
    //               // setReportDataStatus("success")
    //               const reportData = response.data ? response.data : response;
    //               const payload = {
    //                 selectedResource: Object.assign(selectedResource),
    //                 reportData: reportData,
    //                 customFilters: customFilters,
    //                 dataFilters: filterArray,
    //               };

    //               dispatch(updateResourceSourceAndFilter(payload));
    //               // dispatch(updateResourceSource(Object.assign(selectedResource, { reportData: reportData })))
    //               // dispatch(updateDataFilters({ definedFilters: customFilters, filterArray: filterArray }))
    //             })
    //             .catch((error) => {
    //               // setReportDataStatus("error")

    //               console.log(error);
    //             });
    //         } catch (e) {
    //           console.log("JWT authPsign error=====>>", e);
    //           // setReportDataStatus("error")
    //         }
    //       }
    //     } else {
    //       Axios({
    //         method:
    //           selectedResource.source.connectionData.requestValues.httpMethod,
    //         url: selectedResource.source.connectionData.baseUrl + urlParamSring,
    //         data: bodyParamObj,
    //         headers: headerObj,
    //       })
    //         .then(async (response) => {
    //           console.log("CUSTOM FILTER response.data", response);
    //           // setReportDataStatus("success")
    //           // setReportDataObject(response.data ? response.data : response)
    //           const reportData = response.data ? response.data : response;
    //           const payload = {
    //             selectedResource: Object.assign(selectedResource),
    //             reportData: reportData,
    //             customFilters: customFilters,
    //             dataFilters: filterArray,
    //           };

    //           dispatch(updateResourceSourceAndFilter(payload));
    //           // dispatch(updateDataFilters({ dataFilters: filterArray }))
    //         })
    //         .catch((error) => {
    //           // setReportDataStatus("error")
    //           // setReportDataObject(null)
    //           // console.log(error);
    //         });
    //     }
    //   } catch (error) {
    //     // setReportDataStatus("error")
    //     // setReportDataObject(null)
    //     // console.log(error);
    //   }
    // }
  }

  useEffect(() => {
    /* Was: .map() on a possibly-undefined availableFields, then .push() onto the
       undefined result - a resource with aggregations but no fields yet threw here. */
    let availableFieldsSet = (props?.selectedResource?.availableFields || []).map((af) => af);

    props?.selectedResource?.aggregation?.row?.forEach((a, k) => {
      availableFieldsSet.push({
        fieldName: a.displayName,
        checked: true,
        id: Math.floor(Math.random() * 10000) + 1,
        dataType: "number",
      });
    });
    setFields(availableFieldsSet);
  }, [props]);

  const [activeTab, setActiveTab] = useState("global");

  /* The value control for one data filter, typed by the filter's data type. `key` is
     "value" for a single-value filter, or "value1" / "value2" for the two ends of a
     Between. Restores the inputs the redesign dropped - without them every data filter
     saved with a null value.

     Date fields follow the column's own format: if the resource declares a format with
     an hour component the picker shows a time selector too, so the saved string matches
     what the column actually holds. */
  const renderDataFilterValue = (fi, key) => {
    const onChange = (val) => updateDataFilter(fi, key, val);
    const current = fi[key];

    switch (fi.dataType) {
      case "number":
        return (
          <Input type="number" placeholder="Value" value={current ?? ""} onChange={(e) => onChange(e.target.value)} />
        );

      case "date": {
        const format = dateTimeFormatChange.getDateFormat(fi.fieldName).dateAndTimeFormat;
        return (
          <DatePicker
            style={{ width: "100%" }}
            /* Was `moment()` when unset, which put today's date in the box for a filter
               that had no value - an unset filter looked configured. */
            value={current ? moment(current) : null}
            showTime={format === DATE_WITH_HOUR_FORMAT || format === DATE_WITH_HOUR_MINUTES_FORMAT}
            format={format}
            onChange={(date, dateString) => onChange(dateString)}
          />
        );
      }

      /* Not offered by the Data Type select, but boards saved earlier can carry it:
         a dropdown of the distinct values already present in the column. */
      case "select":
        return (
          <Select
            dropdownMatchSelectWidth={false}
            dropdownStyle={WIDE_DROPDOWN}
            style={{ width: "100%" }}
            value={current}
            onChange={onChange}
          >
            {[...new Set((props.reportDataObject || []).map((rd) => rd[fi.fieldName]))].map((o) => (
              <Option key={o} value={o}>
                {o}
              </Option>
            ))}
          </Select>
        );

      default:
        return <Input placeholder="Value" value={current ?? ""} onChange={(e) => onChange(e.target.value)} />;
    }
  };

  /* ── Display Date Filter ──────────────────────────────────────────────────── */

  const datePeriods = buildDatePeriods(definedFilters);
  // One date filter on its own is not a range, so the box stays away entirely.
  const showDateBox = datePeriods.length > 1 || (datePeriods.length === 1 && !datePeriods[0].incomplete);
  // A single range needs no naming; two or more do, so the reader can tell them apart.
  const multiPeriod = datePeriods.length > 1;
  const isMerged = dateDisplay.viewBy !== "separate";

  /* The field label is the parameter name the author typed, because that is the only
     thing tying this control to the request. A blank one falls back rather than
     rendering an empty label. */
  const paramLabel = (filter, fallback) => (filter?.parameterName || "").trim() || fallback;

  /* Nothing is stored until the author actually renames one, so the default adapts to
     how many ranges there are: a lone range is "Select Date Range", two or more are
     "Period 1" / "Period 2". A name that HAS been typed survives either way - it was
     chosen deliberately, so adding a second range must not overwrite it. */
  const periodName = (i) =>
    dateDisplay.periods?.[i]?.name ?? (multiPeriod ? `Period ${i + 1}` : "Select Date Range");

  const setPeriodName = (i, name) => {
    setDateDisplay((current) => {
      const periods = [...(current.periods || [])];
      // periods stays empty until a name is actually edited, so backfill the defaults
      while (periods.length <= i) periods.push({ name: `Period ${periods.length + 1}` });
      periods[i] = { ...periods[i], name };
      return { ...current, periods };
    });
  };

  /* Disabled throughout: a preview of the reader's control, not the control. A plain
     <input>, not antd's - see the note on IconCalendar above. */
  const previewField = (placeholder) => (
    <span className="ddf-preview">
      <input type="text" className="ddf-preview-input" placeholder={placeholder} disabled readOnly />
      <span className="ddf-preview-icon">
        <IconCalendar />
      </span>
    </span>
  );

  const renderPeriod = (period, i) => {
    /* The accent exists only to tell several periods apart. A lone range has nothing
       to be told apart FROM, so it keeps the ordinary label colour and the accent is
       left off - it is still renameable, just not recoloured. */
    const accent = multiPeriod ? PERIOD_ACCENTS[i % PERIOD_ACCENTS.length] : undefined;

    /* Shown for every named range: the periods when there are several, and the lone
       "Select Date Range" of a single merged one - both are renameable the same way.
       The one case with no heading is a single range shown as separate fields, where
       the two parameter names already label the inputs.

       The name reads as a heading and turns into a field only while the pen is
       active - an always-on input looked like another parameter to fill in. */
    const heading = !(multiPeriod || isMerged) ? null : (
      <div className="ddf-period-head">
        {editingPeriod === i ? (
          <input
            autoFocus
            type="text"
            className="ddf-period-input"
            style={{ color: accent }}
            value={periodName(i)}
            onChange={(e) => setPeriodName(i, e.target.value)}
            onBlur={() => setEditingPeriod(null)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "Escape") setEditingPeriod(null);
            }}
          />
        ) : (
          <>
            <span className="ddf-period-name" style={{ color: accent }}>
              {periodName(i)}
            </span>
            <button
              type="button"
              className="ddf-period-edit"
              title={multiPeriod ? "Rename this period" : "Rename this date range"}
              onClick={() => setEditingPeriod(i)}
            >
              <IconPencil />
            </button>
          </>
        )}
      </div>
    );

    return (
      <div className={`ddf-period${period.incomplete ? " ddf-period--incomplete" : ""}`} key={`ddf_p_${i}`}>
        {heading}

        {period.incomplete ? (
          <>
            {previewField("dd/mm/yyyy - dd/mm/yyyy")}
            <span className="ddf-note">Add one more date filter — a range needs both a From and a To.</span>
          </>
        ) : isMerged ? (
          // The label above is the heading now, renameable like any other range.
          previewField("dd/mm/yyyy - dd/mm/yyyy")
        ) : (
          <div className="ddf-split">
            <div className="ddf-field">
              <span className="ddf-label">{paramLabel(period.from, "From Date")}</span>
              {previewField("dd/mm/yyyy")}
            </div>
            <div className="ddf-field">
              <span className="ddf-label">{paramLabel(period.to, "To Date")}</span>
              {previewField("dd/mm/yyyy")}
            </div>
          </div>
        )}
      </div>
    );
  };

  /* Radios are grouped by `name`, which has to be unique per resource or two widgets'
     boxes would share one selection. */
  const viewByName = `ddf-viewby-${props.selectedResource?.id ?? "r"}`;

  /* Any global filter whose name cannot work - two filters writing one parameter.
     A warning (the resource does not declare it) is NOT blocking: that one is still
     savable on purpose. */
  const hasBlockingFilterIssue = definedFilters.some((f) => parameterNameIssue(f)?.kind === "duplicate");

  const dateDisplayBox = !showDateBox ? null : (
    <div className="ddf-box">
      <button type="button" className="ddf-head" onClick={() => setDateBoxOpen(!dateBoxOpen)}>
        <span className="ddf-head-title">
          <IconCalendar /> Display Date Filter
        </span>
        <IconChevron up={dateBoxOpen} />
      </button>

      {dateBoxOpen ? (
        <div className="ddf-body">
          <div className="ddf-viewby">
            <span className="ddf-label">View by</span>
            <div className="ddf-radios">
              <label className="ddf-radio">
                <input
                  type="radio"
                  name={viewByName}
                  checked={isMerged}
                  onChange={() => setDateDisplay((current) => ({ ...current, viewBy: "merge" }))}
                />
                <span>Merge as date range</span>
              </label>
              <label className="ddf-radio">
                <input
                  type="radio"
                  name={viewByName}
                  checked={!isMerged}
                  onChange={() => setDateDisplay((current) => ({ ...current, viewBy: "separate" }))}
                />
                <span>Separate date fields</span>
              </label>
            </div>
          </div>

          {datePeriods.map(renderPeriod)}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="data-filter-wrapper">
      {/* Tab toggle */}
      {props.connectionType === "Rest API" ? (
        <div className="filter-tab-group">
          <button
            className={`filter-tab-btn ${activeTab === "global" ? "active" : ""}`}
            onClick={() => setActiveTab("global")}
          >
            Global Filters
          </button>
          <button
            className={`filter-tab-btn ${activeTab === "data" ? "active" : ""}`}
            onClick={() => setActiveTab("data")}
          >
            Data Filters
          </button>
        </div>
      ) : null}

      {/* ── GLOBAL FILTERS ── */}
      {activeTab === "global" && props.connectionType === "Rest API" ? (
        <div>
          {definedFilters.map((defltr) => (
            <div key={"defltr_" + defltr.id} className="filter-row">
              <div className="filter-row-grid">
                <div className="filter-field">
                  <label>Parameter Type</label>
                  <Select
                    dropdownMatchSelectWidth={false}
                    dropdownStyle={WIDE_DROPDOWN}
                    value={defltr.parameterType}
                    onChange={(val) => updateDefinedFilter(defltr, "parameterType", val)}
                  >
                    <Option value="body">Body</Option>
                    <Option value="query">Query</Option>
                  </Select>
                </div>
                <div className="filter-field">
                  <label>Parameter Name</label>
                  <Input
                    placeholder="Name"
                    value={defltr.parameterName}
                    /* Shown as you type rather than only on Save - the name is the one
                       thing that decides whether a filter does anything at all, and
                       finding out on save meant a round trip for every attempt. */
                    className={parameterNameIssue(defltr)?.kind === "duplicate" ? "filter-input-error" : undefined}
                    onChange={(e) => updateDefinedFilter(defltr, "parameterName", e.target.value)}
                  />
                  {(() => {
                    const issue = parameterNameIssue(defltr);
                    if (!issue) return null;
                    return (
                      <span className={issue.kind === "duplicate" ? "filter-msg-error" : "filter-msg-warn"}>
                        {issue.message}
                      </span>
                    );
                  })()}
                </div>
                <div className="filter-field">
                  <label>Data Type</label>
                  <Select
                    dropdownMatchSelectWidth={false}
                    dropdownStyle={WIDE_DROPDOWN}
                    value={defltr.dataType}
                    onChange={(val) => updateDefinedFilter(defltr, "dataType", val)}
                  >
                    <Option value="text">Text</Option>
                    <Option value="number">Number</Option>
                    <Option value="date">Date</Option>
                    <Option value="strArray">Str Array</Option>
                    <Option value="numArray">Num Array</Option>
                  </Select>
                </div>
              </div>

              {/* Max Length / Min+Max / Date range.
                  These bound what the end user may enter in the runtime filter widget.
                  Every input here was previously value-only with no onChange, so none
                  of it could actually be set. */}
              {defltr.dataType === "number" ? (
                <div className="filter-row-grid-wide">
                  <div className="filter-field">
                    <label>Min</label>
                    <Input
                      type="number"
                      placeholder="Min"
                      value={defltr.config?.min}
                      onChange={(e) => updateDefinedFilter(defltr, "min", e.target.value)}
                    />
                  </div>
                  <div className="filter-field">
                    <label>Max</label>
                    <Input
                      type="number"
                      placeholder="Max"
                      value={defltr.config?.max}
                      onChange={(e) => updateDefinedFilter(defltr, "max", e.target.value)}
                    />
                  </div>
                </div>
              ) : defltr.dataType === "date" ? (
                <div className="filter-row-grid-wide">
                  <div className="filter-field">
                    <label>Range</label>
                    <Select
                      dropdownMatchSelectWidth={false}
                      dropdownStyle={WIDE_DROPDOWN}
                      value={defltr.config?.range || "dynamic"}
                      onChange={(val) => updateDefinedFilter(defltr, "dynamicDateRange", val)}
                    >
                      <Option value="dynamic">Dynamic</Option>
                      <Option value="fixedBefore">Fixed - Before</Option>
                      <Option value="fixedAfter">Fixed - After</Option>
                    </Select>
                  </div>
                  <div className="filter-field">
                    <label>{(defltr.config?.range || "dynamic") === "dynamic" ? "Window" : "Date"}</label>
                    {/* A rolling window is a named preset; a fixed boundary is a real
                        date, so the control swaps with the mode. */}
                    {(defltr.config?.range || "dynamic") === "dynamic" ? (
                      <Select
                        dropdownMatchSelectWidth={false}
                        dropdownStyle={WIDE_DROPDOWN}
                        value={defltr.config?.value || "LastMonth"}
                        onChange={(val) => updateDefinedFilter(defltr, "dynamicDateRangeValue", val)}
                      >
                        <Option value="LastWeek">Last Week</Option>
                        <Option value="Last2Weeks">Last 2 Weeks</Option>
                        <Option value="LastMonth">Last Month</Option>
                        <Option value="LastQuarter">Last Quarter</Option>
                        <Option value="Last6Months">Last 6 Months</Option>
                        <Option value="LastYear">Last Year</Option>
                        <Option value="Any">Any</Option>
                      </Select>
                    ) : (
                      <DatePicker
                        style={{ width: "100%" }}
                        format={DATE_FORMAT}
                        value={defltr.config?.value ? moment(defltr.config.value) : null}
                        onChange={(date, dateString) =>
                          updateDefinedFilter(defltr, "dynamicDateRangeValue", dateString)
                        }
                      />
                    )}
                  </div>
                </div>
              ) : (
                <div className="filter-field">
                  <label>Max Length</label>
                  <Input
                    type="number"
                    placeholder="Max Length"
                    value={defltr.config?.maxLength}
                    onChange={(e) => updateDefinedFilter(defltr, "maxLength", e.target.value)}
                  />
                </div>
              )}

              <div className="filter-field-full">
                <Button type="text" className="filter-remove-btn" onClick={() => deleteDefinedFilterItem(defltr)}>
                  <img src={DeleteIcon} alt="delete-icon" /> Remove
                </Button>
              </div>
            </div>
          ))}
          <Button type="link" className="filter-add-btn" onClick={setDefinedFiltertem}>
            + Add New
          </Button>

          {/* The filters stay above; this sits under them and only previews how their
              dates will be presented to the reader. */}
          {dateDisplayBox}
        </div>
      ) : null}

      {/* ── DATA FILTERS ── */}
      {activeTab === "data" || props.connectionType !== "Rest API" ? (
        <div>
          {dataFilters.map((fi) => (
            <div key={"fil_" + fi.id} className="filter-row">
              <div className="filter-row-grid">
                <div className="filter-field">
                  <label>Field Name</label>
                  <Select
                    showSearch
                    dropdownMatchSelectWidth={false}
                    dropdownStyle={WIDE_DROPDOWN}
                    placeholder="Select"
                    value={fi.fieldName}
                    onChange={(val) => updateDataFilter(fi, "fieldName", val)}
                  >
                    {fields?.map((af, j) => (
                      <Option value={af.fieldName} key={fi.id + "_" + j}>
                        {af.fieldName}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div className="filter-field">
                  <label>Data Type</label>
                  <Select
                    dropdownMatchSelectWidth={false}
                    dropdownStyle={WIDE_DROPDOWN}
                    value={fi.dataType}
                    onChange={(val) => updateDataFilter(fi, "dataType", val)}
                  >
                    <Option value="string">String</Option>
                    <Option value="number">Number</Option>
                    <Option value="date">Date</Option>
                  </Select>
                </div>
                <div className="filter-field">
                  <label>Filter Type</label>
                  {/* lte / gte were dropped in the redesign; the runtime still offers
                      both, so a filter using them could not be configured here. */}
                  <Select
                    dropdownMatchSelectWidth={false}
                    dropdownStyle={WIDE_DROPDOWN}
                    value={fi.filterType}
                    onChange={(val) => updateDataFilter(fi, "filterType", val)}
                  >
                    <Option value="equal">Equal</Option>
                    {fi.dataType !== "string" && (
                      <>
                        <Option value="lt">Less Than</Option>
                        <Option value="gt">Greater Than</Option>
                        <Option value="lte">Less Than Or Equal</Option>
                        <Option value="gte">Greater Than Or Equal</Option>
                        <Option value="between">Between</Option>
                      </>
                    )}
                    {fi.dataType === "string" && <Option value="con">Contains</Option>}
                  </Select>
                </div>
              </div>

              {/* Value. Between needs two, so the single field becomes From / To. */}
              {fi.filterType === "between" ? (
                <div className="filter-row-grid-wide">
                  <div className="filter-field">
                    <label>From Value</label>
                    {renderDataFilterValue(fi, "value1")}
                  </div>
                  <div className="filter-field">
                    <label>To Value</label>
                    {renderDataFilterValue(fi, "value2")}
                  </div>
                </div>
              ) : (
                <div className="filter-field">
                  <label>Value</label>
                  {renderDataFilterValue(fi, "value")}
                </div>
              )}

              <div className="filter-field-full">
                <Input
                  placeholder="Display Name"
                  value={fi.displayName}
                  onChange={(e) => updateDataFilter(fi, "displayName", e.target.value)}
                  style={{ flex: 1 }}
                />
                <Button className="filter-remove-btn" onClick={() => deleteDataFilterItem(fi)}>
                  <img src={DeleteIcon} alt="delete-icon" /> Remove
                </Button>
              </div>
            </div>
          ))}

          <Button type="link" className="filter-add-btn" onClick={setDataFiltertem}>
            + Add New
          </Button>
        </div>
      ) : null}
      <div className="filter-actions">
        {/* Nothing to save while a filter is in a state that cannot work. The check
            runs live, so the button releases as soon as the name is fixed - and the
            save path still refuses these anyway, this just stops the dead click. */}
        <Button
          type="primary"
          disabled={hasBlockingFilterIssue}
          title={hasBlockingFilterIssue ? "Fix the highlighted parameter name first" : undefined}
          onClick={() => saveFilters(dataFilters, definedFilters)}
        >
          Save Filters
        </Button>
        <Button onClick={clearFilters}>Reset</Button>
      </div>
    </div>
  );
}
